// Lib
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import type {
  ColDef,
  GridReadyEvent,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy,
} from "ag-grid-community";
import { ICellRendererParams } from "ag-grid-community";

// Local
import { DbRead } from "../../database";
import CommissionsListPage from "./CommissionsListPage";
import * as urls from "../../urls";

//Styles
import "react-h5-audio-player/lib/styles.css";
import "../../assets/css/AudioPlayer.css";
import { CommissionReportData } from "../../models/estimate";
import { DateTime } from "luxon";
import currencyFormatter from "../../currency";
import DateRangePicker from "../../components/DateRangePicker";
import { RangeValue } from "@react-types/shared";
import {
  CalendarDate,
  CalendarDateTime,
  getLocalTimeZone,
  toCalendarDate,
  today,
  ZonedDateTime,
} from "@internationalized/date";
import { logger } from "../../logging";
import LoadingClipboardAnimation from "../../components/LoadingClipBoardAnimation";
import SearchBox from "../../components/SearchBox";
import { useToastMessageStore } from "../../store/toast-messages";

interface Props {
  siteKey: string;
}

/* Stuff for Table Component */
const headerStyles =
  "px-2 py-0 text-xs font-medium text-center text-gray-500 uppercase";

const commonColProps = {
  flex: 1,
  resizable: true,
  filter: true,
  sortable: true,
  headerClass: headerStyles,
};

// function customerRenderer(
//   params: CustomCellRendererProps<CommissionReportData>,
// ): JSX.Element {
//   const id = params.data?.invoice?.customerID;
//   const name = params.data?.invoice?.name ?? "Unknown";
//   if (id) {
//     return (
//       <Link
//         to={`${urls.CUSTOMERS_URL}/${id}`}
//         className="text-blue-500 underline"
//       >
//         {name}
//       </Link>
//     );
//   } else {
//     return <div className="text-black">{name}</div>;
//   }
// }

const renderTimestamp = (params: ICellRendererParams): JSX.Element => {
  return (
    <div>
      {typeof params.data.date === "string"
        ? DateTime.fromISO(params.data.date).toFormat("LL/dd/yy hh:mm a")
        : ""}
    </div>
  );
};

const renderInvoice = (params: ICellRendererParams): JSX.Element => {
  if (params.data.invoiceNumber == "" || params.data.invoiceNumber == "--") {
    return <div>{"--"}</div>;
  } else {
    return (
      <div>
        {params.data.invoiceNumber} -{" "}
        {currencyFormatter(params.data.invoiceTotal, "USD")}
      </div>
    );
  }
};

const renderCurrency = (params: ICellRendererParams): JSX.Element => {
  return <div>{currencyFormatter(params.value, "USD")}</div>;
};

const columnDefs: ColDef<CommissionReportData>[] = [
  {
    field: "date",
    headerName: "Date",
    cellRenderer: renderTimestamp,
    width: 180,
  },
  {
    field: "message",
    headerName: "Message",
    // cellRenderer: renderTimestamp,
    width: 180,
  },
  {
    field: "employee",
    headerName: "Employee",
    // cellRenderer: renderTimestamp,
    width: 180,
  },
  {
    field: "taskType",
    headerName: "Task Type",
    // cellRenderer: renderTimestamp,
    width: 180,
  },
  {
    field: "invoiceNumber",
    headerName: "Invoice",
    cellRenderer: renderInvoice,
    width: 70,
  },
  {
    field: "customerName",
    headerName: "Customer",
    // cellRenderer: customerRenderer,
    width: 70,
  },
  {
    field: "commissionableSalesBase",
    headerName: "Base Sales",
    cellRenderer: renderCurrency,
    width: 70,
  },
  {
    field: "baseCommission",
    headerName: "Base Commission",
    cellRenderer: renderCurrency,
    width: 70,
  },
  {
    field: "commissionableSalesAddOn",
    headerName: "Add-On",
    cellRenderer: renderCurrency,
    width: 70,
  },
  {
    field: "addOnCommission",
    headerName: "Add-On Commission",
    cellRenderer: renderCurrency,
    width: 70,
  },
  {
    field: "totalCommission",
    headerName: "Total Commission",
    cellRenderer: renderCurrency,
    width: 70,
    cellStyle: {
      fontWeight: "bold",
    },
  },
];

export default function CommissionsListContainer(props: Props): JSX.Element {
  const siteKey = props.siteKey;

  const addMessage = useToastMessageStore((state) => state.addToastMessage);

  const [commissionData, setCommissionData] = useState<CommissionReportData[]>(
    [],
  );
  const [commissionDataLoading, setCommissionDataLoading] =
    useState<boolean>(false);
  const [range, setRange] = useState<RangeValue<CalendarDate>>({
    start: today(getLocalTimeZone()).subtract({ days: 2 }),
    end: today(getLocalTimeZone()).subtract({ days: 1 }),
  });

  useEffect(() => {
    async function getCommissions() {
      setCommissionDataLoading(true);
      try {
        const data = await DbRead.commissions.getCommissionReportData(
          siteKey,
          range.start.toString(),
          range.end.toString(),
        );
        setCommissionData(Object.values(data));
        setCommissionDataLoading(false);
      } catch (e) {
        logger.error("Error in getCommissions", e);
        addMessage({
          message: "Error fetching commissions",
          type: "error",
          dialog: false,
          id: "commissions-list-container-error",
        });
        setCommissionDataLoading(false);
      }
    }

    getCommissions();
  }, [siteKey, range]);

  // #region ---- GRID STUFF ----
  const gridRef = useRef<AgGridReact<CommissionReportData>>(null);
  const [gridReady, setGridReady] = useState(false);

  useLayoutEffect(() => {
    if (gridRef.current && gridReady) {
      gridRef.current!.api.sizeColumnsToFit();
      gridRef.current.api.setGridOption("suppressScrollOnNewData", true);
    }
  }, [gridReady]);

  const handleGridReady = useCallback(
    () => (_event: GridReadyEvent) => {
      setGridReady(true);
    },
    [],
  );

  function handleDateRangeChange(
    value: RangeValue<CalendarDate | ZonedDateTime | CalendarDateTime>,
  ) {
    logger.debug(
      `Invoked handleDateRangeChange for range: ${value.start.month}-${value.start.day} to ${value.end.month}-${value.end.day}`,
    );
    try {
      const convertedStart = toCalendarDate(value.start);
      const convertedEnd = toCalendarDate(value.end);
      const converted = { start: convertedStart, end: convertedEnd };
      setRange(converted);
      logger.debug(
        `set range to ${converted.start.month}-${converted.start.day} to ${converted.end.month}-${converted.end.day}`,
      );
    } catch (e) {
      // TODO: Handle this error better
      if (e instanceof Error) {
        console.log("Error in handleDateRangeChange", e);
        logger.error("Error in handleDateRangeChange", e);
      }
    }
  }

  // See [Auto Size Strategy](https://ag-grid.com/react-data-grid/column-sizing/#auto-size-columns-to-fit-grid)
  const autoSizeStrategy = useMemo<
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy
  >(() => {
    return {
      type: "fitGridWidth",
      defaultMinWidth: 100,
      columnLimits: [
        {
          colId: "ID",
          minWidth: 150,
        },
      ],
    };
  }, []);

  const dateRangePicker = (
    <DateRangePicker value={range} onChange={handleDateRangeChange} />
  );

  const searchBox = (
    <SearchBox
      onInput={() => {
        gridRef.current!.api.setQuickFilter(
          (document.getElementById("filter-text-box") as HTMLInputElement)
            .value,
        );
      }}
      widthClasses="w-full sm:max-w-sm"
    />
  );

  // #endregion

  return (
    <CommissionsListPage>
      {{
        // audioPlayer: audioPlayer,
        // tableControlsSlot: (
        //   <div>Defaults to showing calls within the past 30 days</div>
        // ),
        DateRangePicker: dateRangePicker,
        SearchBox: searchBox,
        tableSlot: commissionDataLoading ? (
          <div className="flex h-full flex-col items-center justify-center">
            <LoadingClipboardAnimation />
          </div>
        ) : (
          <AgGridReact<CommissionReportData>
            reactiveCustomComponents
            ref={gridRef}
            onGridReady={handleGridReady}
            autoSizeStrategy={autoSizeStrategy}
            defaultColDef={commonColProps}
            className="-z-10 mt-5 shadow"
            getRowId={(data) => data.data.id}
            rowData={Object.values(commissionData) ?? []}
            animateRows={false}
            pagination={true}
            rowSelection="single"
            rowHeight={50}
            cacheQuickFilter={true}
            columnDefs={columnDefs}
            // onFirstDataRendered={(params) => {
            //   if (didComeFromBack) {
            //     const currentPage = localStorage.getItem(
            //       "callsListContainerCurrentPage",
            //     );
            //
            //     const pageToNavigate = JSON.parse(
            //       currentPage ??
            //         JSON.stringify(params.api.paginationGetCurrentPage()),
            //     );
            //     params.api.paginationGoToPage(pageToNavigate);
            //   }
            // }}
            // onPaginationChanged={(params) => {
            //   if (params.newPage) {
            //     const currentPage = params.api.paginationGetCurrentPage();
            //     localStorage.setItem(
            //       "callsListContainerCurrentPage",
            //       JSON.stringify(currentPage),
            //     );
            //   }
            // }}
            onCellClicked={(event) => {
              const { data } = event;
              console.log(data);
              if (data?.task?.craftRecordID) {
                const craftRecordID = data.task.craftRecordID.split("/")[3];
                // navigate(`${urls.WORK_RECORD_AND_TASKS_URL}/${craftRecordID}`);
                const url = `${urls.WORK_RECORD_AND_TASKS_URL}/${craftRecordID}`;
                return window.open(url, "_blank");
              }
            }}
          />
        ),
      }}
    </CommissionsListPage>
  );
}
